/**
 * SectionErrorBoundary
 * ────────────────────────────────────────────────────────────────
 * Catches runtime crashes inside a Dashboard section so the entire
 * app no longer goes blank when a button handler throws. The user
 * reported "some buttons crash the system when clicked" — this
 * boundary keeps the chrome (sidebar / header) intact and lets the
 * student click "Try Again" to remount the section, or jump back
 * to their previous section.
 */
import React from "react";

export default class SectionErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Surface to the console — the screen still shows the fallback.
    // eslint-disable-next-line no-console
    console.error("[YLC] Section crashed:", error, info?.componentStack);
  }

  reset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    const { sectionLabel = "this section", onBack } = this.props;
    return (
      <div
        data-testid="section-error-boundary"
        style={{
          maxWidth: "700px",
          margin: "40px auto",
          padding: "30px",
          background: "linear-gradient(135deg, #2a1a1a 0%, #1a0a0a 100%)",
          border: "1px solid #8B0000",
          borderRadius: "12px",
          color: "#eee",
          textAlign: "center",
        }}
      >
        <div style={{ fontSize: "2.5rem", marginBottom: "10px" }}>⚠️</div>
        <h3 style={{ color: "#FF6B6B", marginTop: 0 }}>
          Something went wrong in {sectionLabel}
        </h3>
        <p style={{ color: "#bbb", lineHeight: 1.6 }}>
          We caught the error before it could crash the whole platform. You
          can retry, or return to the dashboard.
        </p>
        {this.state.error?.message && (
          <pre
            style={{
              textAlign: "left",
              background: "#000",
              padding: "10px",
              borderRadius: "6px",
              color: "#FFA07A",
              fontSize: "0.78rem",
              overflowX: "auto",
              marginTop: "10px",
            }}
          >
            {String(this.state.error.message).slice(0, 400)}
          </pre>
        )}
        <div
          style={{
            display: "flex",
            gap: "10px",
            justifyContent: "center",
            marginTop: "20px",
            flexWrap: "wrap",
          }}
        >
          <button
            data-testid="error-boundary-retry-btn"
            onClick={this.reset}
            style={{
              background: "linear-gradient(135deg, #FFD700 0%, #FFA500 100%)",
              color: "#000",
              padding: "10px 22px",
              borderRadius: "8px",
              border: "none",
              fontWeight: "bold",
              cursor: "pointer",
            }}
          >
            🔄 Try Again
          </button>
          {onBack && (
            <button
              data-testid="error-boundary-back-btn"
              onClick={() => {
                this.reset();
                onBack();
              }}
              style={{
                background: "#333",
                color: "#fff",
                padding: "10px 22px",
                borderRadius: "8px",
                border: "1px solid #555",
                cursor: "pointer",
              }}
            >
              ← Back to Dashboard
            </button>
          )}
        </div>
      </div>
    );
  }
}
